import { createRoot } from "react-dom/client";
import type { ImagingExam } from "@/core/imaging/types";
import {
  formatImagingReportSummary,
  imagingExamConsultationLabel,
} from "@/core/imaging/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";

interface ImagingReportPrintSheetProps {
  exam: ImagingExam;
}

function ImagingReportPrintSheet({ exam }: ImagingReportPrintSheetProps) {
  const patient = getPatientById(exam.patientId);
  const validatedLabel = exam.completedAt
    ? new Date(exam.completedAt).toLocaleString("fr-FR")
    : new Date().toLocaleString("fr-FR");

  return (
    <div className="ph-invoice-print-sheet">
      <div className="ph-invoice-header">
        <div>
          <div className="ph-invoice-brand">ITaaS Hospitol</div>
          <div className="ph-invoice-sub">Imagerie — compte-rendu radiologique</div>
        </div>
        <div className="ph-invoice-meta text-end">
          <div className="ph-invoice-title">COMPTE-RENDU</div>
          <div className="fw-semibold">{exam.examNumber}</div>
          <div className="small text-muted">{validatedLabel}</div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="ph-invoice-section-title">Patient</div>
          <div className="fw-semibold">{patient ? patientFullName(patient) : "—"}</div>
          {patient && <div className="small text-muted">Dossier {patient.fileNumber}</div>}
        </div>
        <div className="col-md-6">
          <div className="ph-invoice-section-title">Examen</div>
          <div className="fw-semibold">
            {exam.examType}
            {exam.bodyPart ? ` — ${exam.bodyPart}` : ""}
          </div>
          {exam.clinicalIndication && (
            <div className="small text-muted mt-1">Indication : {exam.clinicalIndication}</div>
          )}
          {exam.prescriber && (
            <div className="small text-muted mt-1">Prescripteur : {exam.prescriber}</div>
          )}
          {exam.consultationId && (
            <div className="small text-muted mt-1">
              Origine : {imagingExamConsultationLabel(exam.consultationId)}
            </div>
          )}
        </div>
      </div>

      {exam.reportTechnique.trim() && (
        <>
          <div className="ph-invoice-section-title">Technique</div>
          <div className="border rounded p-3 mb-3" style={{ whiteSpace: "pre-wrap" }}>
            {exam.reportTechnique}
          </div>
        </>
      )}

      {exam.reportFindings.trim() && (
        <>
          <div className="ph-invoice-section-title">Constatations</div>
          <div className="border rounded p-3 mb-3" style={{ whiteSpace: "pre-wrap" }}>
            {exam.reportFindings}
          </div>
        </>
      )}

      {exam.reportConclusion.trim() && (
        <>
          <div className="ph-invoice-section-title">Conclusion</div>
          <div className="border rounded p-3 mb-3" style={{ whiteSpace: "pre-wrap" }}>
            {exam.reportConclusion}
          </div>
        </>
      )}

      {!exam.reportTechnique.trim() &&
        !exam.reportFindings.trim() &&
        !exam.reportConclusion.trim() && (
          <>
            <div className="ph-invoice-section-title">Compte-rendu</div>
            <div className="border rounded p-3 mb-3" style={{ whiteSpace: "pre-wrap", minHeight: "8rem" }}>
              {formatImagingReportSummary(exam)}
            </div>
          </>
        )}

      {exam.reportFileName && (
        <div className="small text-muted mb-3">Pièce jointe : {exam.reportFileName}</div>
      )}

      <div className="small text-muted">
        Document généré par ITaaS Hospitol — usage médical interne.
      </div>
    </div>
  );
}

export function printImagingReport(exam: ImagingExam): void {
  const portal = document.createElement("div");
  portal.className = "ph-print-portal";
  portal.setAttribute("aria-hidden", "true");
  document.body.appendChild(portal);

  const root = createRoot(portal);
  root.render(<ImagingReportPrintSheet exam={exam} />);

  window.setTimeout(() => {
    window.print();
    root.unmount();
    portal.remove();
  }, 150);
}

export { ImagingReportPrintSheet };
